import { ReactElement, useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import Heading from 'components/v2/Heading'
import { Button } from 'components/v2/Button'
import { isEmpty } from 'lodash'
import { getUnprocessableErrors } from '@10xlabs/utils'
import { Formik, useFormikContext } from 'formik'
import { Column } from 'components/v2/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import FormLayout from 'components/v2/FormLayout'
import { Row } from 'components/v2/Row'
import {
  GTBusinessClientAutocomplete,
  GTBusinessCustomerTypeInput,
  GTBusinessSiteSuggestion
} from 'gql/types.generated'
import { BusinessClientAutocomplete } from 'features/BusinessClients/components/BusinessClientAutocomplete'
import { BusinessSiteAutocomplete } from 'features/BusinessSites/components/BusinessSiteAutocomplete'
import { Select } from 'components/v2/Select'
import { FilePreview, InputFile } from 'components/v2/InputFile'
import { ApolloError, useMutation } from '@apollo/client'
import { usePushFileToS3 } from 'utils/file'
import { VALIDATE_BUSINESS_CUSTOMER_CSV } from 'features/BusinessCustomer/queries-legacy'
import { IBusinessCustomerCSVFormValues } from 'features/BusinessCustomer/types'
import { schema } from './schema'
import Modal from 'components/v2/Modal'
import { getCSVBusinessCustomerErrors, hasCSVError } from 'features/BusinessCustomer/utils'

const messages = defineMessages({
  customerTypePlaceholder: {
    id: 'businessCustomer.form.customerType.placeholder',
    defaultMessage: 'Select type'
  },
  clientPlaceholder: {
    id: 'businessCustomer.form.client.placeholder',
    defaultMessage: 'Select business client'
  },
  sitePlaceholder: {
    id: 'businessCustomer.form.site.placeholder',
    defaultMessage: 'Select business site'
  }
})

const TimelineWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const StyledFormContent = styled(FormLayout.Content)`
  height: 100%;
`

const ActionsContainer = styled.div`
  margin-top: auto;
  margin-inline: auto;
`

const BusinessCustomerCSVForm = ({
  closeDrawer,
  loading,
  error
}: {
  error?: ApolloError
  loading?: boolean
  closeDrawer: () => void
}): ReactElement => {
  const { handleSubmit, values, setFieldValue, setFieldError, touched, errors, setFieldTouched, setValues } =
    useFormikContext<IBusinessCustomerCSVFormValues>()

  const pushFileToS3 = usePushFileToS3()
  const [fileUploadLoading, setFileUploadLoading] = useState(false)
  const [validateCSV] = useMutation(VALIDATE_BUSINESS_CUSTOMER_CSV)
  const intl = useIntl()

  const uploadFile = async (file: File): Promise<string | undefined> => {
    try {
      const path = await pushFileToS3(file)
      return path
    } catch (error) {
      await setFieldTouched('file', true)
      setFieldError('file', 'Error uploading file. Try again')
    }
  }
  const handleFileChange = async (file: File | ''): Promise<void> => {
    if (!file) {
      await setFieldTouched('file', false)
      setValues({ ...values, filePath: '', file: '' })
      return
    }
    try {
      setValues({ ...values, filePath: '', file: '' })
      setFileUploadLoading(true)
      const path = await uploadFile(file)
      if (path) {
        const res = await validateCSV({
          variables: {
            input: {
              path
            }
          }
        })
        const isValidFile = res.data
        if (isValidFile) {
          setValues({ ...values, filePath: path, file })
        }
      }
    } catch (error) {
      await setFieldTouched('file', true)
      // @ts-expect-error ts(2322) FIXME
      setFieldError('file', getCSVBusinessCustomerErrors(error, intl))
    } finally {
      setFileUploadLoading(false)
    }
  }

  return (
    <StyledFormContent as="form" onSubmit={handleSubmit}>
      <Row noMargin>
        <Column>
          <BusinessClientAutocomplete
            id="businessClient"
            value={values.businessClient.name}
            onChange={(businessClient: GTBusinessClientAutocomplete): void => {
              setValues({ ...values, businessClient: businessClient, businessSite: { id: '', name: '' } })
            }}
            placeholder={intl.formatMessage(messages.clientPlaceholder)}
            label={<FormattedMessage id="businessCustomerCSVForm.form.client.label" defaultMessage="Business client" />}
            touched={touched.businessClient?.id}
            errors={errors.businessClient?.id}
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column>
          <BusinessSiteAutocomplete
            id="businessSite"
            value={values.businessSite.name}
            onChange={(businessSite: GTBusinessSiteSuggestion): void => {
              setFieldValue('businessSite', businessSite)
            }}
            placeholder={intl.formatMessage(messages.sitePlaceholder)}
            label={<FormattedMessage id="businessCustomerCSVForm.form.site.label" defaultMessage="Business site" />}
            touched={touched.businessSite?.id}
            errors={errors.businessSite?.id}
            disabled={isEmpty(values.businessClient.id)}
            businessClientId={values.businessClient.id}
            optional
          />
        </Column>
      </Row>
      <Row noMargin>
        <Column>
          <Select
            id="customerType"
            value={values.customerType}
            onChange={(e): void => {
              setFieldValue('customerType', e.target.value)
            }}
            touched={Boolean(touched.customerType)}
            errors={errors.customerType}
            label={<FormattedMessage id="dictionary.type" defaultMessage="Type" />}
          >
            <option value="">{intl.formatMessage(messages.customerTypePlaceholder)}</option>
            <option value={GTBusinessCustomerTypeInput.Associate}>
              <FormattedMessage id="businessCustomer.form.customerType.associate" defaultMessage="Associate" />
            </option>
          </Select>
        </Column>
      </Row>
      <Row>
        <Column>
          <InputFile
            touched={touched.file}
            errors={errors.file}
            onChange={handleFileChange}
            accept={{
              'text/csv': []
            }}
          />
          {!isEmpty(values.file) && (
            <FilePreview
              file={values.file as File}
              filename={(values.file as File).name}
              onRemoveFile={(): void => {
                setFieldValue('file', '')
              }}
            />
          )}
        </Column>
      </Row>
      <ActionsContainer>
        {hasCSVError(error) && <ErrorMessage>{getCSVBusinessCustomerErrors(error, intl)}</ErrorMessage>}
        {!hasCSVError(error) && error && (
          <ErrorMessage errors={getUnprocessableErrors(error)}>
            <FormattedMessage
              id="businessCustomerCSVForm.form.error"
              defaultMessage="There was an error while creating your Customer"
            />
          </ErrorMessage>
        )}
        <FormLayout.Actions>
          <Button kind="cancel" onClick={(): void => closeDrawer()}>
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" disabled={isEmpty(values.file)} loading={loading || fileUploadLoading}>
            <FormattedMessage id="dictionary.upload" defaultMessage="Upload" />
          </Button>
        </FormLayout.Actions>
      </ActionsContainer>
    </StyledFormContent>
  )
}

interface BusinessCustomerModalProps {
  setIsOpen: (value: boolean) => void
  onSubmit: (values: IBusinessCustomerCSVFormValues) => void
  error?: ApolloError
  loading?: boolean
}

export const BusinessCustomerCSVModal = ({
  setIsOpen,
  onSubmit,
  error,
  loading
}: BusinessCustomerModalProps): ReactElement => {
  const intl = useIntl()
  const initialFormValues: IBusinessCustomerCSVFormValues = {
    customerType: GTBusinessCustomerTypeInput.Associate,
    file: '',
    businessSite: {
      id: '',
      name: ''
    },
    businessClient: {
      id: '',
      name: ''
    },
    filePath: ''
  }

  return (
    <Modal.Container onClose={(): void => setIsOpen(false)}>
      <TimelineWrapper>
        <Modal.Header>
          <Heading.H5>
            <FormattedMessage id="businessCustomerCSVForm.uploadCsv.title" defaultMessage="Upload CSV" />
          </Heading.H5>
        </Modal.Header>
        <Formik initialValues={initialFormValues} validationSchema={schema(intl)} onSubmit={onSubmit}>
          {(): ReactElement => (
            <BusinessCustomerCSVForm loading={loading} error={error} closeDrawer={(): void => setIsOpen(false)} />
          )}
        </Formik>
      </TimelineWrapper>
    </Modal.Container>
  )
}
